import React from "react";
import { useLocation } from "react-router-dom";

function OrderDetails() {
  const location = useLocation();
  const { order } = location.state || {};

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return '';
    }
    
    if (imagePath.startsWith('http')) return imagePath;
    
    const cleanPath = imagePath.replace(/^(\.?\/?)?/, '');
    
    // Handle different environments
    let baseUrl;
    const { hostname, port, protocol } = window.location;
    
    if (hostname === 'localhost' && port === '3000') {
      // React dev server on port 3000, APIs on port 80
      baseUrl = `${protocol}//${hostname}`;
    } else if (hostname === 'localhost' && !port) {
      // React build served from same domain as APIs
      baseUrl = window.location.origin;
    } else {
      // Production environment
      baseUrl = window.location.origin;
    }
    
    return `${baseUrl}/apis/${cleanPath}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!order) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">❌</div>
          <p className="text-red-500 text-xl mb-2">No order details found</p>
          <p className="text-gray-500">Please go back and select an order to view details.</p>
        </div>
      </div>
    );
  }

  const cartItems = JSON.parse(order.cart_items);

  return (
    <section className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-8 text-white">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Order Details</h1>
            <p className="text-blue-100">Order #{order.order_id}</p>
          </div>

          {/* Order Summary */}
          <div className="p-6 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Order Date</h3>
                  <p className="text-lg font-semibold text-gray-900">
                    {formatDate(order.created_at)}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Payment Status</h3>
                  <p className={`text-lg font-semibold ${
                    order.payment_status === "Completed"
                      ? "text-green-600"
                      : order.payment_status === "Pending"
                      ? "text-yellow-600"
                      : "text-red-600"
                  }`}>
                    {order.payment_status}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Delivery Status</h3>
                  <p className={`text-lg font-semibold ${
                    order.delivery_status === "Delivered"
                      ? "text-green-600"
                      : order.delivery_status === "Processing"
                      ? "text-blue-600"
                      : "text-gray-600"
                  }`}>
                    {order.delivery_status}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500">Total Amount</h3>
                  <p className="text-2xl font-bold text-gray-900">
                    ₹{order.total_amount}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Cart Items */}
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6">Order Items</h2>
            <div className="space-y-6">
              {cartItems.map((item, index) => (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-6 p-4 bg-gray-50 rounded-lg border border-gray-200"
                >
                  <div className="w-full sm:w-32 h-32 flex-shrink-0">
                    <img
                      src={getImageUrl(item.product_image)}
                      alt={item.product_name}
                      className="w-full h-full object-cover rounded-lg shadow-sm"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTI4IiBoZWlnaHQ9IjEyOCIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjY0IiB5PSI2NCIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkb21pbmFudC1iYXNlbGluZT0ibWlkZGxlIiBmaWxsPSIjOGE5YmFiIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.product_name}
                    </h3>
                    {item.variant && (
                      <p className="text-gray-600 mb-2">
                        <span className="font-medium">Variant:</span> {item.variant}
                      </p>
                    )}
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-500">Quantity:</span>
                        <span className="ml-2 text-gray-900">{item.quantity}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-500">Price:</span>
                        <span className="ml-2 text-gray-900">₹{item.price}</span>
                      </div>
                    </div>
                    <div className="mt-3">
                      <span className="font-medium text-gray-500">Subtotal:</span>
                      <span className="ml-2 text-lg font-semibold text-gray-900">
                        ₹{(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
              <span className="text-2xl font-bold text-gray-900">₹{order.total_amount}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default OrderDetails;